import React, { FC, Fragment, useState } from 'react';
import { Box, Grid, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import { EngagementHistoryFormat, UserEngagementHistoryQueryVariables } from 'api';
import { EmptyState, PaddedPaper } from 'components';
import { useCurrentUser, useDateRange, useNotify, useEngagementHistory } from 'hooks';

import { View } from '../types';
import GridLoader from './GridLoader';
import ListLoader from './ListLoader';
import Filter from './Filter';
import Card from './Card';
import Table from './Table';

const useStyles = makeStyles({
  emptyStatePaper: {
    height: 400,
  },
  emptyStateContainer: {
    height: '100%',
  },
});

const NetworkHistory: FC = () => {
  const classes = useStyles();
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const dateRangeOptions = useDateRange({ includeToday: true });

  const [view, setView] = useState(View.Grid);
  const [variables, setVariables] = useState<UserEngagementHistoryQueryVariables>({
    exclude_internal: true,
    format: EngagementHistoryFormat.Chronological,
    offset: 0,
    limit: 24,
    user_id: currentUser.id,
  });

  const { results, sentinelRef, isEmpty, isExhausted, isInitialLoad } = useEngagementHistory(
    {
      ...variables,
      timestamp_end: moment(dateRangeOptions.endDate).endOf('day').unix(),
      timestamp_start: moment(dateRangeOptions.startDate).startOf('day').unix(),
    },
    {
      onError: notify.queryError,
    }
  );

  function renderHistory() {
    if (isInitialLoad) {
      return view === View.Grid ? <GridLoader /> : <ListLoader />;
    }

    if (isEmpty) {
      return (
        <Paper className={classes.emptyStatePaper}>
          <Grid
            className={classes.emptyStateContainer}
            container
            justify="center"
            alignItems="center"
          >
            <Grid item xs>
              <EmptyState fullWidth name="networkHistory" />
            </Grid>
          </Grid>
        </Paper>
      );
    }

    if (view === View.Grid) {
      return (
        <Fragment>
          <Grid container spacing={2}>
            {results.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card format={variables.format} item={item} />
              </Grid>
            ))}
          </Grid>
          {!isExhausted && (
            <div ref={sentinelRef}>
              <GridLoader />
            </div>
          )}
        </Fragment>
      );
    }

    if (view === View.List) {
      return (
        <Table
          format={variables.format}
          results={results}
          isInitialLoad={isInitialLoad}
          isExhausted={isExhausted}
          sentinelRef={sentinelRef}
        />
      );
    }

    return null;
  }

  return (
    <Fragment>
      <PaddedPaper>
        <Filter
          dateRangeOptions={dateRangeOptions}
          view={view}
          variables={variables}
          onChange={setVariables}
          onChangeView={setView}
        />
      </PaddedPaper>
      <Box marginTop={2}>{renderHistory()}</Box>
    </Fragment>
  );
};

export default NetworkHistory;
